// Las reglas de SISTEMA (las 51 sensibilidades de siempre) vistas como reglas: sus umbrales, cómo se
// guardan, qué efecto tiene cada una y por dónde avisa. Sin React.
//
// El backend las COMPONE en vivo desde las tablas de siempre (`AlertRuleCatalogService`) y editarlas
// es el mismo POST que la pantalla clásica: una sola verdad, el clásico ve exactamente lo mismo.
import {
    BUDGET_INDICATORS,
    CONVERSIONS_DAILY_INDICATOR,
    CPA_INDICATORS,
    CURRENT_ACOS_VS_LAST_MONTH,
    CURRENT_PERIOD_COST_VS_TIME_COMPLETION_INDICATOR,
    CURRENT_ROAS_VS_LAST_MONTH,
    EXCEED_BUDGET_INDICATOR,
    FUNDS_INDICATOR,
    GOAL_1_INDICATOR,
    GOAL_2_INDICATOR,
    ODS_VS_LAST_7_DAYS_INDICATOR,
    ODS_VS_PLATFORM_BUDGET_INDICATOR,
    ODS_VS_TODAY_INDICATOR,
    ODS_VS_YESTERDAY_INDICATOR,
    TODAY_COST_INDICATOR,
    TODAY_INDICATOR_NAMES,
    UNCOVERED_SPEND_INDICATOR,
} from '../../indicators';
import {formatInputNumber, isActiveRule, parseValue} from './rulesModel';

export const SECTION_BUDGET = 'BUDGET';
export const SECTION_CONVERSIONS = 'CONVERSIONS';
export const SECTION_TODAY = 'TODAY';
export const SECTION_FUNDS = 'FUNDS';
export const SECTION_GOALS = 'GOALS';
export const SECTION_FLEX = 'FLEX';
export const SECTION_ECOMMERCE = 'ECOMMERCE';
export const SECTION_ORDER = [SECTION_BUDGET, SECTION_CONVERSIONS, SECTION_TODAY, SECTION_FUNDS,
    SECTION_GOALS, SECTION_FLEX, SECTION_ECOMMERCE];

export const LEVEL_BAD = 'BAD';
export const LEVEL_CRITIC = 'CRITIC';
const LEVEL_GOOD = 'GOOD';
const LEVEL_SUCCESS = 'SUCCESS';

export const UNIT_DAYS = 'DAYS';

const FLEX_PREFIX = 'FLEX_INDICATOR';

export function isFlexIndicator(indicator) {
    return String(indicator || '').includes(FLEX_PREFIX);
}

/** El indicador de una regla de sistema (`system:<IND>` o el campo `indicator`). */
export function indicatorOf(rule) {
    if (!rule) return null;
    if (rule.indicator) return rule.indicator;
    const id = String(rule.id || '');
    return id.startsWith('system:') ? id.slice('system:'.length) : null;
}

/**
 * Los indicadores con UN solo umbral que no son de «Hoy»: «Inversión que la línea deja afuera» avisa desde
 * un porcentaje (20 % por defecto) y por debajo no hay «Malo» (decisión de fede, 17/10). El catálogo lo
 * manda con `thresholds: [{level: 'CRITIC', …}]` y `paired_levels: {}`.
 */
export const SINGLE_THRESHOLD_INDICATORS = [UNCOVERED_SPEND_INDICATOR];

export function isSingleThresholdIndicator(indicator) {
    return SINGLE_THRESHOLD_INDICATORS.includes(indicator);
}

/**
 * Qué niveles guarda el backend para cada indicador, y cuáles salen del mismo número.
 *
 * Es el MISMO espejo que hace la pantalla clásica (`TwoLevelIndicatorSensitivity` /
 * `SingleLevelIndicatorSensitivity`), que es la que escribió todo lo que hay en la base: con dos
 * niveles visibles, Malo arrastra a Bueno y Crítico a Exitoso en los indicadores que pintan los dos
 * lados («el lado bueno se pinta en verde con los mismos números»); con un solo valor (Hoy), ese
 * número va a todos los niveles del indicador. Mandar un nivel que el indicador no tiene crearía una
 * fila de más (`_get_or_create_sensitivity_value_obj`), así que no se manda.
 *
 * Los de UN solo umbral (`SINGLE_THRESHOLD_INDICATORS`) se resuelven ANTES de `paired_levels`: con
 * `paired_levels: {}` el plan del backend arma dos niveles, y ahí `{}` quiere decir «sin lado verde», no
 * «sin Malo» (lo usan los indicadores de dos niveles para apagar el espejo de la tabla de acá).
 */
export function levelPlan(indicator, pairedLevels = null) {
    if (isSingleThresholdIndicator(indicator)) return {single: true, levels: [LEVEL_CRITIC]};
    const fromBackend = planFromPairedLevels(indicator, pairedLevels);
    if (fromBackend) return fromBackend;
    if (indicator === TODAY_COST_INDICATOR) return {single: true, levels: [LEVEL_CRITIC]};
    if (TODAY_INDICATOR_NAMES.includes(indicator)) return {single: true, levels: [LEVEL_CRITIC, LEVEL_SUCCESS]};
    const mirrored = indicator === CONVERSIONS_DAILY_INDICATOR
        || CPA_INDICATORS.includes(indicator)
        || indicator === CURRENT_ROAS_VS_LAST_MONTH
        || indicator === CURRENT_ACOS_VS_LAST_MONTH
        || indicator === GOAL_1_INDICATOR
        || indicator === GOAL_2_INDICATOR
        || isFlexIndicator(indicator);
    if (mirrored) {
        return {single: false, mirror: {[LEVEL_BAD]: [LEVEL_BAD, LEVEL_GOOD], [LEVEL_CRITIC]: [LEVEL_CRITIC, LEVEL_SUCCESS]}};
    }
    return {single: false, mirror: {[LEVEL_BAD]: [LEVEL_BAD], [LEVEL_CRITIC]: [LEVEL_CRITIC]}};
}

/**
 * El plan que sale de `paired_levels` del catálogo (`{BAD: 'GOOD', CRITIC: 'SUCCESS'}`, sólo con los
 * niveles que el indicador tiene): es la misma regla del clásico, dicha por el backend, así que manda
 * sobre la tabla escrita a mano de arriba (C-ac-fe-centro-24). `null` si no vino.
 */
function planFromPairedLevels(indicator, pairedLevels) {
    if (!pairedLevels || typeof pairedLevels !== 'object') return null;
    const mirrorOf = (level) => [level, ...(pairedLevels[level] ? [pairedLevels[level]] : [])];
    if (TODAY_INDICATOR_NAMES.includes(indicator)) return {single: true, levels: mirrorOf(LEVEL_CRITIC)};
    return {single: false, mirror: {[LEVEL_BAD]: mirrorOf(LEVEL_BAD), [LEVEL_CRITIC]: mirrorOf(LEVEL_CRITIC)}};
}

// El 2.º aviso de inversión puede quedar vacío: el backend lo acepta (`ALERT_SENSITIVITIES_NULL_VALUES`)
// y sin él la regla sólo da el 1.º. En el resto, el vacío se guardaba como 0 (lo que la pantalla
// clásica dejaba pasar): acá no se acepta.
function acceptsEmpty(indicator, level) {
    return indicator === EXCEED_BUDGET_INDICATOR && level === LEVEL_CRITIC;
}

function thresholdValue(rule, level) {
    const found = (rule.thresholds || []).find((item) => item.level === level);
    return found ? found.value : undefined;
}

/** Los niveles que se ven y se editan: Malo y Crítico, o sólo Crítico (Hoy y los de un solo umbral). */
export function visibleThresholdLevels(rule) {
    return levelPlan(indicatorOf(rule), rule.paired_levels).single ? [LEVEL_CRITIC] : [LEVEL_BAD, LEVEL_CRITIC];
}

/**
 * Los campos que se editan: Malo y Crítico (o Advertencia y Crítico en días), o uno solo en Hoy y en los
 * de un solo umbral. El valor viene HUMANO (15 = 15 %, 7 = 7 días), como lo manda el catálogo.
 */
export function thresholdFields(rule, {locale = 'es'} = {}) {
    const indicator = indicatorOf(rule);
    const unit = (rule.thresholds && rule.thresholds[0] && rule.thresholds[0].unit) || 'PERCENTAGE';
    return visibleThresholdLevels(rule).map((level) => {
        const value = thresholdValue(rule, level);
        return {
            level,
            unit,
            value: formatInputNumber(value, locale),
            optional: acceptsEmpty(indicator, level),
        };
    });
}

export function thresholdLabelKey(rule, level) {
    const indicator = indicatorOf(rule);
    // Un solo umbral: no es «Crítico» de una escala de dos, es desde dónde avisa.
    if (isSingleThresholdIndicator(indicator)) return 'alerts_center.rules.threshold_alerts_from';
    if (indicator === EXCEED_BUDGET_INDICATOR) {
        return level === LEVEL_BAD ? 'indicators.first_warning' : 'indicators.second_warning';
    }
    if (level === LEVEL_BAD) {
        const unit = rule.thresholds && rule.thresholds[0] && rule.thresholds[0].unit;
        return unit === UNIT_DAYS ? 'alerts_center.rules.threshold_warning' : 'alerts_center.rules.threshold_bad';
    }
    return 'alerts_center.rules.threshold_critic';
}

/**
 * `{nivel: clave}` de lo que no se puede guardar. Vacío no (salvo el 2.º aviso). En porcentaje Malo
 * va antes que Crítico; en días al revés: Advertencia con MÁS días que Crítico (el saldo se acaba
 * después). El backend no valida nada de esto: guarda lo que le llegue.
 */
export function validateThresholds(rule, values, {locale = 'es'} = {}) {
    const indicator = indicatorOf(rule);
    const fields = thresholdFields(rule, {locale});
    const errors = {};
    fields.forEach((field) => {
        const raw = values[field.level];
        const empty = raw === undefined || raw === null || String(raw).trim() === '';
        if (empty) {
            if (!field.optional) errors[field.level] = 'alerts_center.rules.validation_empty';
            return;
        }
        const number = parseValue(raw, locale);
        if (number === null || number < 0) errors[field.level] = 'alerts_center.rules.validation_number';
    });
    if (fields.length === 2 && !errors[LEVEL_BAD] && !errors[LEVEL_CRITIC]) {
        const bad = parseValue(values[LEVEL_BAD], locale);
        const critic = parseValue(values[LEVEL_CRITIC], locale);
        if (bad !== null && critic !== null) {
            const unit = fields[0].unit;
            if (unit === UNIT_DAYS && !(bad > critic)) {
                errors[LEVEL_BAD] = 'alerts_center.rules.validation_order_days';
            } else if (unit !== UNIT_DAYS && !(bad < critic)) {
                errors[LEVEL_BAD] = indicator === EXCEED_BUDGET_INDICATOR
                    ? 'alerts_center.rules.validation_order_warnings'
                    : 'alerts_center.rules.validation_order';
            }
        }
    }
    return errors;
}

export function initialThresholdValues(rule, {locale = 'es'} = {}) {
    const values = {};
    thresholdFields(rule, {locale}).forEach((field) => { values[field.level] = field.value; });
    return values;
}

export function thresholdsChanged(rule, values, {locale = 'es'} = {}) {
    return thresholdFields(rule, {locale}).some((field) => {
        const before = parseValue(field.value, locale);
        const after = parseValue(values[field.level], locale);
        return before !== after;
    });
}

// Las fracciones, como siempre (0,15 = 15 %). También en días: el motor compara `valor × 100` contra
// los días que quedan (`FundsAlert.levels`), así que 7 días se guardan como 0,07.
function toFraction(value, locale) {
    const number = parseValue(value, locale);
    if (number === null) return null;
    return Number((number / 100).toFixed(6));
}

/**
 * El cuerpo del POST parcial: SÓLO este indicador, con sus dos claves (el command las lee sin
 * default). Va el mapa pelado, como lo manda la pantalla clásica (ver `alertRulesApi`).
 */
export function systemSavePayload(rule, values, isActive, {locale = 'es'} = {}) {
    const indicator = indicatorOf(rule);
    const plan = levelPlan(indicator, rule.paired_levels);
    const sensitivityValues = [];
    if (values === null) {
        // Sólo el interruptor: la lista vacía no toca ningún umbral (el command recorre
        // `sensitivity_values` y no hay nada que recorrer). Reenviar los umbrales que trajo la lista
        // pisaría con `null` —y el backend lo guarda como 0— cualquiera que no viniera.
        return {[indicator]: {is_active: isActive !== false, sensitivity_values: []}};
    }
    if (plan.single) {
        const value = toFraction(values[LEVEL_CRITIC], locale);
        plan.levels.forEach((level) => sensitivityValues.push({level, value}));
    } else {
        [LEVEL_BAD, LEVEL_CRITIC].forEach((visible) => {
            const value = toFraction(values[visible], locale);
            plan.mirror[visible].forEach((level) => sensitivityValues.push({level, value}));
        });
    }
    return {[indicator]: {is_active: isActive !== false, sensitivity_values: sensitivityValues}};
}

/**
 * Lo que cambió al guardar los umbrales, para pintarlo sin recargar: `{id, thresholds}` y NADA más.
 * El `is_active` no viaja: es el de cuando se apretó «Guardar», y un interruptor tocado mientras el
 * POST viajaba lo dejaría como estaba (C-ac-fe-centro-12).
 */
export function savedThresholdsPatch(rule, values, {locale = 'es'} = {}) {
    const thresholds = (rule.thresholds || []).map((item) => {
        if (!Object.prototype.hasOwnProperty.call(values, item.level)) return item;
        return {...item, value: parseValue(values[item.level], locale)};
    });
    return {id: rule.id, thresholds};
}

// ------------------------------------------------------------- la tuerca del Tablero (v2) --------
// Con las alertas convertidas (README v2 §4.4) la tuerca de la columna edita el semáforo: lo mismo que
// el panel de arriba, sobre el GET de la columna. Así la tuerca usa los mismos campos (`ThresholdFields`),
// la misma validación y el mismo espejo de niveles que la pantalla clásica.

const UNIT_PERCENTAGE = 'PERCENTAGE';

/**
 * El semáforo de una columna (`GET agency/dashboard-column-color/<col>/`) como una regla de Dashbo: el
 * indicador y sus umbrales en HUMANO. El GET los manda en fracción, también los días: el motor compara
 * `valor × 100` contra los días que quedan (`FundsAlert.levels`), así que 4 días se guardan 0,04.
 */
export function columnRule(data) {
    const unit = data && data.unit === 'days' ? UNIT_DAYS : UNIT_PERCENTAGE;
    const levels = (data && Array.isArray(data.levels)) ? data.levels : [];
    return {
        id: `system:${data ? data.indicator : ''}`,
        indicator: data ? data.indicator : null,
        thresholds: levels.map((item) => ({
            level: item.level,
            unit,
            value: item.value === null || item.value === undefined ? null : Number((Number(item.value) * 100).toFixed(4)),
        })),
    };
}

/**
 * Los `levels` del PUT de la tuerca, en HUMANO (15 = 15 %, 4 = 4 días; el backend los pasa a fracción), con
 * el espejo de la pantalla clásica (Malo arrastra a Bueno y Crítico a Exitoso donde el indicador pinta los
 * dos lados) y SÓLO los niveles que el indicador tiene: mandar uno de más crearía una fila.
 */
export function columnLevelsPayload(rule, values, {locale = 'es'} = {}) {
    const plan = levelPlan(indicatorOf(rule), rule.paired_levels);
    const existing = new Set((rule.thresholds || []).map((item) => item.level));
    const levels = [];
    const push = (level, raw) => {
        if (existing.size && !existing.has(level)) return;
        const text = raw === undefined || raw === null ? '' : String(raw).trim();
        levels.push({level, value: text === '' ? null : parseValue(text, locale)});
    };
    if (plan.single) plan.levels.forEach((level) => push(level, values[LEVEL_CRITIC]));
    else [LEVEL_BAD, LEVEL_CRITIC].forEach((visible) => plan.mirror[visible].forEach((level) => push(level, values[visible])));
    return levels;
}

// ------------------------------------------------------------------- «¿Cómo se calcula?» ---------

const HOW_KEYS = {
    budget: 'alerts_center.how.budget_deviation',
    costVsTime: 'alerts_center.how.cost_vs_time',
    ods: 'alerts_center.how.ods',
    exceed: 'alerts_center.how.exceed_budget',
    vsPeriod: 'alerts_center.how.vs_period',
    today: 'alerts_center.how.today',
    funds: 'alerts_center.how.funds',
    goals: 'alerts_center.how.goals',
    flex: 'alerts_center.how.flex',
    uncovered: 'alerts_center.how.uncovered_spend',
};

const ODS_INDICATORS = [ODS_VS_YESTERDAY_INDICATOR, ODS_VS_LAST_7_DAYS_INDICATOR, ODS_VS_TODAY_INDICATOR,
    ODS_VS_PLATFORM_BUDGET_INDICATOR];

/**
 * Lo que dice debajo de los umbrales, según la regla (fede, 09/10): si avisa y en qué nivel, o que sólo
 * cambia el color; y el lado verde, sólo si la regla lo tiene. Era un texto para todas («Crítico avisa,
 * si la regla avisa»), que en una que sólo pinta no se entendía.
 */
export function thresholdHintKeys(rule) {
    // Un solo umbral (rojo y avisa; por debajo, gris): su texto dice las dos cosas, sin «Malo» ni «Crítico».
    if (isSingleThresholdIndicator(indicatorOf(rule)) && rule.notifies !== false) {
        return ['alerts_center.rules.threshold_hint_single'];
    }
    const levels = Array.isArray(rule.notifies_on_levels) ? rule.notifies_on_levels : [];
    let first = 'alerts_center.rules.threshold_hint_notifies_critic';
    if (rule.notifies === false) first = 'alerts_center.rules.threshold_hint_paint_only';
    else if (levels.includes(LEVEL_BAD)) first = 'alerts_center.rules.threshold_hint_notifies_bad';
    const plan = levelPlan(indicatorOf(rule), rule.paired_levels);
    const green = plan.single ? plan.levels.length > 1
        : Object.values(plan.mirror).some((mirrored) => mirrored.length > 1);
    return green ? [first, 'alerts_center.rules.threshold_hint_green'] : [first];
}

export function howKey(rule) {
    const indicator = indicatorOf(rule);
    if (isFlexIndicator(indicator)) return HOW_KEYS.flex;
    if (TODAY_INDICATOR_NAMES.includes(indicator)) return HOW_KEYS.today;
    if (indicator === FUNDS_INDICATOR) return HOW_KEYS.funds;
    if (indicator === GOAL_1_INDICATOR || indicator === GOAL_2_INDICATOR) return HOW_KEYS.goals;
    if (indicator === EXCEED_BUDGET_INDICATOR) return HOW_KEYS.exceed;
    if (indicator === UNCOVERED_SPEND_INDICATOR) return HOW_KEYS.uncovered;
    if (indicator === CURRENT_PERIOD_COST_VS_TIME_COMPLETION_INDICATOR) return HOW_KEYS.costVsTime;
    if (ODS_INDICATORS.includes(indicator)) return HOW_KEYS.ods;
    if (BUDGET_INDICATORS.includes(indicator)) return HOW_KEYS.budget;
    return HOW_KEYS.vsPeriod;
}

// ------------------------------------------------------------------ por dónde avisa ---------------

// La preferencia de correo de cada tipo de alerta en «Mis alertas»
// (`GET user/<id>/module/marketing/alerts`). Las que no figuran no mandan correo nunca.
const EMAIL_FLAG_BY_INDICATOR = {
    [ODS_VS_YESTERDAY_INDICATOR]: 'critic_ods_vs_yesterday_alert_is_active',
    [ODS_VS_TODAY_INDICATOR]: 'critic_ods_vs_today_alert_is_active',
    [ODS_VS_LAST_7_DAYS_INDICATOR]: 'critic_ods_vs_last_7_days_alert_is_active',
    [CURRENT_PERIOD_COST_VS_TIME_COMPLETION_INDICATOR]: 'critic_cost_vs_time_alert_is_active',
    [CURRENT_ROAS_VS_LAST_MONTH]: 'critic_roas_vs_last_period_alert_is_active',
    [CURRENT_ACOS_VS_LAST_MONTH]: 'critic_acos_vs_last_period_alert_is_active',
    [EXCEED_BUDGET_INDICATOR]: 'budget_exceeded_alert_is_active',
    [GOAL_1_INDICATOR]: 'goal_alert_is_active',
    [GOAL_2_INDICATOR]: 'goal_alert_is_active',
    // Tanda «Control de líneas» (15/10): con un backend que no manda la clave, la fila cae al texto
    // genérico del correo, como cualquier preferencia que no se pudo leer.
    [ODS_VS_PLATFORM_BUDGET_INDICATOR]: 'ods_vs_platform_budget_alert_is_active',
    [UNCOVERED_SPEND_INDICATOR]: 'uncovered_spend_alert_is_active',
};
const FLEX_EMAIL_FLAG = 'flex_indicator_alert_is_active';

// Los tipos de alerta por correo de «Mis alertas» que corresponden a reglas de esta lista (las de
// URL caída e impresiones son de otra cosa). Hasta el 14/10 eran el «te llegan N de M tipos»; desde
// C-07 el recuadro cuenta reglas (`RulesList`) y esto sólo dice si se pudo leer «Mis alertas».
export const EMAIL_ALERT_FLAGS = [...new Set(Object.values(EMAIL_FLAG_BY_INDICATOR)), FLEX_EMAIL_FLAG];

export function emailFlagFor(indicator) {
    if (isFlexIndicator(indicator)) return FLEX_EMAIL_FLAG;
    return EMAIL_FLAG_BY_INDICATOR[indicator] || null;
}

/** `{enabled, total}` de «Mis alertas», o `null` si el GET no respondió (el texto queda genérico). */
export function emailSummary(userEmail) {
    if (!userEmail) return null;
    const known = EMAIL_ALERT_FLAGS.filter((flag) => typeof userEmail[flag] === 'boolean');
    if (known.length === 0) return null;
    return {enabled: known.filter((flag) => userEmail[flag] === true).length, total: known.length};
}

/**
 * Los cuatro rótulos de una regla de sistema —Tablero, Correo, Webhook, Bitácora— con su estado real:
 * `{on, tipKey, tipParams}`.
 *
 * `live`:
 *  - `userEmail`: el payload de «Mis alertas» de quien mira (`null` si no se pudo leer).
 *  - `webhook`: `{configured, selected: [ids]}` de la tarjeta del webhook ya guardada, que pisa lo
 *    que trajo la lista (un evento apagado ahí se ve en la fila sin recargar). `null` = no se sabe.
 */
export function systemEffects(rule, {userEmail = null, webhook = null} = {}) {
    const active = rule.is_active !== false;
    const notifies = active && rule.notifies === true;
    const delivery = rule.delivery || {};
    const email = delivery.email || {};
    const hook = delivery.webhook || {};

    const dashboard = !active
        ? {on: false, tipKey: 'alerts_center.rules.effect_paused'}
        : rule.paints_dashboard === true
            ? {on: true, tipKey: 'alerts_center.rules.effect_dashboard_on'}
            : {on: false, tipKey: 'alerts_center.rules.effect_dashboard_off'};

    let emailEffect;
    if (!active) emailEffect = {on: false, tipKey: 'alerts_center.rules.effect_paused'};
    else if (!notifies) emailEffect = {on: false, tipKey: 'alerts_center.rules.effect_paint_only'};
    else if (email.has_email === false) emailEffect = {on: false, tipKey: 'alerts_center.rules.effect_email_none'};
    else {
        const flag = emailFlagFor(indicatorOf(rule));
        const mine = flag && userEmail && typeof userEmail[flag] === 'boolean' ? userEmail[flag] : null;
        const agency = typeof email.enabled_users === 'number' && typeof email.total_users === 'number'
            ? {enabled: email.enabled_users, total: email.total_users} : null;
        if (mine === false) {
            emailEffect = {on: false, tipKey: 'alerts_center.rules.effect_email_off', agency};
        } else if (mine === true) {
            emailEffect = {on: true, tipKey: 'alerts_center.rules.effect_email_on', agency};
        } else {
            emailEffect = {on: true, tipKey: 'alerts_center.rules.effect_email_generic', agency};
        }
    }

    let webhookEffect;
    if (!active) webhookEffect = {on: false, tipKey: 'alerts_center.rules.effect_paused'};
    else if (!notifies) webhookEffect = {on: false, tipKey: 'alerts_center.rules.effect_paint_only'};
    else if (!hook.event) webhookEffect = {on: false, tipKey: 'alerts_center.rules.effect_webhook_no_event'};
    else {
        const configured = webhook ? webhook.configured : hook.configured === true;
        const enabled = webhook && Array.isArray(webhook.selected)
            ? webhook.selected.includes(hook.event) : hook.event_enabled !== false;
        if (!configured) webhookEffect = {on: false, tipKey: 'alerts_center.rules.effect_webhook_none'};
        else if (!enabled) webhookEffect = {on: false, tipKey: 'alerts_center.rules.effect_webhook_off'};
        else webhookEffect = {on: true, tipKey: 'alerts_center.rules.effect_webhook_on'};
    }

    const bitacora = !active
        ? {on: false, tipKey: 'alerts_center.rules.effect_paused'}
        : notifies && delivery.bitacora !== false
            ? {on: true, tipKey: 'alerts_center.rules.effect_bitacora_on'}
            : {on: false, tipKey: 'alerts_center.rules.effect_paint_only'};

    // Una que no avisa y sin color: ninguno de los cuatro caminos hace nada, y los cuatro lo dicen igual
    // (C-ac-fe-centro-26). En pausa manda la pausa, de arriba.
    if (active && isColorOffSilentRule(rule)) {
        const off = {on: false, tipKey: 'alerts_center.rules.effect_color_off_silent'};
        return {dashboard: off, email: off, webhook: off, bitacora: off};
    }

    return {dashboard, email: emailEffect, webhook: webhookEffect, bitacora};
}

/**
 * Una regla de Dashbo que nunca avisa y pinta su columna del Tablero (las de «Hoy», por ejemplo). Es lo
 * que es la regla, no su estado: apagada sigue siendo de las que sólo pintan (`paints_dashboard` no
 * depende de `is_active`). Hacen falta los dos valores explícitos: un backend que no manda `notifies` no
 * esconde los canales, y una que hoy no pinta no «sólo pinta» (C-ac-fe-centro-26).
 */
export function isPaintOnlyRule(rule) {
    return Boolean(rule) && rule.notifies === false && rule.paints_dashboard === true;
}

/**
 * Una que no avisa y a la que la agencia le sacó el color a todas sus columnas desde el semáforo del
 * Tablero (F0 C-10). Las que no avisan pintan siempre por catálogo, así que en una fila ese
 * `paints_dashboard: false` sólo puede venir de ahí. Hoy no hace nada visible.
 */
export function isColorOffSilentRule(rule) {
    return Boolean(rule) && rule.notifies === false && rule.paints_dashboard === false;
}

export const ALERT_RULE_EVENT = 'ALERT_RULE_TRIGGERED';

/**
 * ¿El correo de una regla propia puede llegarle a quien mira (`me`, su id)? «A mí» le llega a quien la
 * creó y «a estas personas», a las elegidas. «A los asignados» y «a toda la agencia» dependen de quién
 * ve cada cliente, que acá no se sabe: cuentan. Sin saber quién mira, también.
 */
export function emailReachesViewer(rule, me) {
    if (me === null || me === undefined || me === '') return true;
    const recipients = (rule && rule.delivery && rule.delivery.recipients) || {};
    if (recipients.type === 'ME') return Boolean(rule.created_by) && String(rule.created_by.id) === String(me);
    if (recipients.type === 'USERS') return (recipients.user_ids || []).some((id) => String(id) === String(me));
    return true;
}

/**
 * ¿La regla puede mandarle correo a quien mira, esté prendida o no? Es el «de M» de «Correo: recibes N
 * de M» (C-07): una propia con correo que lo incluye, o una de Dashbo que avisa con correo.
 */
export function emailCanReachViewer(rule, me) {
    if (!rule) return false;
    if (rule.kind === 'CUSTOM') {
        const channels = (rule.delivery && rule.delivery.channels) || [];
        return channels.includes('EMAIL') && emailReachesViewer(rule, me);
    }
    const email = (rule.delivery && rule.delivery.email) || {};
    return rule.notifies === true && email.has_email !== false;
}

/**
 * Los rótulos de una regla personalizada: nunca pinta el Tablero; avisa por lo que la regla eligió. El
 * correo se dice desde quien mira (`me`), como el de las de Dashbo: el de una regla que le escribe a
 * otras personas no le llega (C-07).
 */
export function customEffects(rule, {webhook = null, me = null} = {}) {
    const active = isActiveRule(rule);
    const channels = (rule.delivery && rule.delivery.channels) || [];
    const paused = {on: false, tipKey: 'alerts_center.rules.effect_paused'};
    const dashboard = {on: false, tipKey: 'alerts_center.rules.effect_dashboard_custom'};
    if (!active) return {dashboard, email: paused, webhook: paused, bitacora: paused};

    let email;
    if (!channels.includes('EMAIL')) email = {on: false, tipKey: 'alerts_center.rules.effect_channel_not_chosen'};
    else if (!emailReachesViewer(rule, me)) email = {on: false, tipKey: 'alerts_center.rules.effect_email_others'};
    else email = {on: true, tipKey: 'alerts_center.rules.effect_email_custom'};

    let hook;
    if (!channels.includes('WEBHOOK')) hook = {on: false, tipKey: 'alerts_center.rules.effect_channel_not_chosen'};
    // `webhook` en null = no se pudo leer (o todavía no llegó): ni «sale» ni «no sale»
    // (C-ac-fe-centro-13). La tarjeta de canales dice lo mismo.
    else if (!webhook) hook = {on: false, unknown: true, tipKey: 'alerts_center.rules.effect_webhook_unknown'};
    else if (!webhook.configured) hook = {on: false, tipKey: 'alerts_center.rules.effect_webhook_none'};
    else if (Array.isArray(webhook.selected) && !webhook.selected.includes(ALERT_RULE_EVENT)) {
        hook = {on: false, tipKey: 'alerts_center.rules.effect_webhook_off'};
    } else hook = {on: true, tipKey: 'alerts_center.rules.effect_webhook_on'};

    return {dashboard, email, webhook: hook, bitacora: {on: true, tipKey: 'alerts_center.rules.effect_bitacora_on'}};
}
